// sign-in-errors — maps Firebase Auth errors from signInWithEmailAndPassword
// to a message in the reader's language.
//
// F-10: this file held a `Record<string, [he, en]>` table — nine strings
// that the `rtl ? … : …` sweep never saw, because they were not
// ternaries. It is the same bug in a different shape: two languages
// hard-coded in a pair, and an Armenian or Russian reader getting the
// English one. The table now holds dictionary KEYS and the caller
// passes `t`.
//
// Pure module (no Firebase import) so it is unit-testable in vitest's node
// environment; Onboarding imports it directly. Accounts are created by the
// owner in the Firebase Console — there is no self-service sign-up and no
// in-app password reset — so the constant help line points users at the
// administrator instead of offering either.

export interface FormattedSignInError {
  /** Firebase error code (`auth/…`) when present, else ''. */
  code: string;
  /** Short top-line message shown in the red error box. */
  message: string;
  /** Diagnostic detail (raw code / message) shown smaller beneath it.
   * Empty when nothing useful is available. */
  detail: string;
}

/** Public host users sign in from. Named in the unauthorized-domain copy
 * so a non-technical user can forward the exact fix to ops. */
export const PUBLIC_APP_HOST = 'app.mycrews.io';

/** Dictionary key for the constant help line under the form. */
export const SIGN_IN_HELP_LINE_KEY = 'authNoAccountContactAdmin';

// Wrong e-mail, wrong password and unknown e-mail all map to the same
// line on purpose: Firebase itself collapses them into
// auth/invalid-credential when e-mail enumeration protection is on, and
// telling a caller which half was wrong leaks account existence.
const BAD_CREDENTIALS = 'authBadCredentials';

const MESSAGES: Record<string, string> = {
  'auth/invalid-credential': BAD_CREDENTIALS,
  'auth/wrong-password': BAD_CREDENTIALS,
  'auth/user-not-found': BAD_CREDENTIALS,
  'auth/invalid-email': 'authInvalidEmail',
  'auth/user-disabled': 'authUserDisabled',
  'auth/too-many-requests': 'authTooManyRequests',
  'auth/network-request-failed': 'authNetworkFailed',
  // The only one that names the host; `{host}` is filled in below so the
  // fix can be forwarded to ops verbatim in any language.
  'auth/unauthorized-domain': 'authUnauthorizedDomain',
};

function readCode(err: unknown): string {
  if (typeof err !== 'object' || err == null) return '';
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

export function formatSignInError(
  err: unknown,
  t: (key: string) => string,
): FormattedSignInError {
  const code = readCode(err);
  const rawMsg = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  const known = MESSAGES[code];
  if (known) {
    return { code, message: t(known).replace('{host}', PUBLIC_APP_HOST), detail: code };
  }
  // Unknown Firebase code: show the code itself — it is what ops need.
  if (code) {
    return { code, message: code, detail: rawMsg && rawMsg !== code ? rawMsg : '' };
  }
  // Not a Firebase error (thrown locally, e.g. empty fields): its message.
  return {
    code: '',
    message: rawMsg || t('authSignInFailed'),
    detail: '',
  };
}
